import * as React from "react";
import { Text } from "@uikit";
import {
  GolfOrderLine,
  GolfOrderLineCart_Types,
} from "@kalabo/golfsavers-common";
import { TransferOrderLine } from "@kalabo/golfsavers-common";
import XE from "@components/common/xe";
import { Hole_Option } from "@kalabo/golfsavers-common";

interface CartItemPriceBreakdownProps {
  golf_line: GolfOrderLine;
  transfer_line: TransferOrderLine;
  display_currency: string;
}

const CartItemPriceBreakdown = ({
  golf_line,
  transfer_line,
  display_currency,
}: CartItemPriceBreakdownProps) => {
  const calculateGreenFee = () => {
    let val = 0;

    // Green Fee
    val =
      (golf_line?.green_fee?.cost + golf_line?.green_fee?.margin) *
        golf_line?.where_when_who?.number_of_golfers +
      Number(
        golf_line?.where_when_who.number_of_golfers % 2
          ? golf_line?.green_fee?.surcharge || 0
          : 0
      );

    return val;
  };

  const calculateCaddies = () => {
    let val = 0;

    // Caddies
    if (
      golf_line?.caddies?.enabled &&
      golf_line?.caddies?.payment_option !== null &&
      golf_line?.caddies?.payment_option !== "ON_ARRIVAL"
    ) {
      val += golf_line?.caddies?.total;
    }

    return val;
  };

  const calculateCarts = () => {
    let val = 0;

    // Carts
    golf_line?.carts?.types?.map(
      // eslint-disable-next-line array-callback-return
      (cart_type: GolfOrderLineCart_Types, idx: any) => {
        val += golf_line?.carts?.types[idx]?.total;
      }
    );

    return val;
  };

  const calculateTransfers = () => {
    let val = 0;

    // Carts
    if (transfer_line?.vehicle?.vehicle?.id) {
      val = transfer_line?.vehicle?.total * transfer_line?.vehicle?.quantity;
    }

    return val;
  };

  const getTransferDetails = () => {
    let val = "";

    if (transfer_line?.where_when_who?.type === "RETURN") {
      val = `${transfer_line?.transfer_legs?.outbound?.from_name} > ${transfer_line?.transfer_legs?.outbound?.to_name} `;
    }

    if (transfer_line?.where_when_who?.type === "CUSTOM") {
      val = `${transfer_line?.transfer_legs?.outbound?.from_name} > ${transfer_line?.transfer_legs?.outbound?.to_name} > ${transfer_line?.transfer_legs?.inbound?.to_name}`;
    }

    val += ` in ${transfer_line?.vehicle?.vehicle?.name.en}`;
    return val;
  };

  const teeTime = React.useMemo(() => {
    return (
      <>
        {" "}
        · {golf_line?.tee_time?.start_time} - {golf_line?.tee_time?.end_time}{" "}
      </>
    );
  }, [golf_line?.tee_time]);

  const holeOptions = React.useMemo(() => {
    if (golf_line?.hole_options?.selected_option !== "ANY") {
      let hole_option = golf_line?.hole_options?.hole_options?.find(
        (option: Hole_Option) => {
          return option.id === golf_line?.hole_options?.selected_option;
        }
      );
      return <> · {hole_option?.description} </>;
    }
    return null;
  }, [golf_line?.hole_options?.selected_option]);

  return (
    <>
      <div className="flex justify-between">
        <div className="flex flex-col gap-0.5 max-w-[66%]">
          <Text size={15} color="black" className="!mb-[2px]">
            Green Fees
          </Text>
          <Text size={13} leading={1.3} color="black60" className="mb-0">
            {golf_line.where_when_who.number_of_golfers} x{" "}
            {golf_line?.product?.name?.en}
            {holeOptions}
            {teeTime}
          </Text>
        </div>
        <div className="flex flex-col gap-0.5">
          <Text
            size={15}
            weight="semibold"
            color="black90"
            className="!mb-[2px] text-right"
          >
            <XE
              base_currency={golf_line?.supplier?.currency}
              currency={display_currency}
              value={calculateGreenFee()}
            />
          </Text>
          <Text
            size={13}
            color="black60"
            leading={1.3}
            className="mb-0 text-right"
          >
            {display_currency !== golf_line?.supplier?.currency && (
              <>
                <XE
                  base_currency={golf_line?.supplier?.currency}
                  currency={golf_line?.supplier?.currency}
                  value={calculateGreenFee()}
                />
              </>
            )}
          </Text>
        </div>
      </div>
      {golf_line?.caddies?.enabled && golf_line?.caddies?.payment_option && (
        <div className="flex justify-between">
          <div className="flex flex-col gap-0.5 max-w-[66%]">
            <Text size={15} color="black" className="!mb-[2px]">
              Caddies
            </Text>
            <Text size={13} leading={1.3} color="black60" className="mb-0">
              {golf_line.where_when_who.number_of_golfers} x{" "}
              {golf_line?.caddies?.type === "SINGLE_CADDIE"
                ? "single caddie"
                : "shared caddies"}
            </Text>
          </div>
          <div className="flex flex-col gap-0.5">
            <Text
              size={15}
              weight="semibold"
              color="black90"
              className="!mb-[2px] text-right"
            >
              {calculateCaddies() > 0 ? (
                <>
                  <XE
                    base_currency={golf_line?.supplier?.currency}
                    currency={display_currency}
                    value={calculateCaddies()}
                  />
                </>
              ) : (
                <>PAY AT COURSE</>
              )}
            </Text>
            <Text
              size={13}
              color="black60"
              leading={1.3}
              className="mb-0 text-right"
            >
              {calculateCaddies() > 0 ? (
                <>
                  {display_currency !== golf_line?.supplier?.currency && (
                    <>
                      <XE
                        base_currency={golf_line?.supplier?.currency}
                        currency={golf_line?.supplier?.currency}
                        value={calculateCaddies()}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <XE
                    base_currency={golf_line?.supplier?.currency}
                    currency={golf_line?.supplier?.currency}
                    value={golf_line?.caddies?.total}
                  />
                </>
              )}
            </Text>
          </div>
        </div>
      )}

      {golf_line?.carts?.enabled && golf_line?.carts?.payment_option && (
        <div className="flex justify-between">
          <div className="flex flex-col gap-0.5 max-w-[66%]">
            <Text size={15} color="black" className="!mb-[2px]">
              Carts
            </Text>
            <Text size={13} leading={1.3} color="black60" className="mb-0">
              {golf_line?.carts?.types.map((type: GolfOrderLineCart_Types) => {
                if (type.quantity === 0) return null;
                return (
                  <>
                    {type.quantity} x{" "}
                    {type.type === "SINGLE_CART"
                      ? "single cart"
                      : "shared cart"}
                  </>
                );
              })}
            </Text>
          </div>
          <div className="flex flex-col gap-0.5">
            <Text
              size={15}
              weight="semibold"
              color="black90"
              className="!mb-[2px] text-right"
            >
              {golf_line?.carts?.enabled &&
              golf_line?.carts?.payment_option !== null &&
              golf_line?.carts?.payment_option !== "ON_ARRIVAL" ? (
                <>
                  <XE
                    base_currency={golf_line?.supplier?.currency}
                    currency={display_currency}
                    value={calculateCarts()}
                  />
                </>
              ) : (
                <>PAY AT COURSE</>
              )}
            </Text>
            <Text
              size={13}
              color="black60"
              leading={1.3}
              className="mb-0 text-right"
            >
              {golf_line?.carts?.enabled &&
              golf_line?.carts?.payment_option !== null &&
              golf_line?.carts?.payment_option !== "ON_ARRIVAL" ? (
                <>
                  {display_currency !== golf_line?.supplier?.currency && (
                    <>
                      <XE
                        base_currency={golf_line?.supplier?.currency}
                        currency={golf_line?.supplier?.currency}
                        value={calculateCarts()}
                      />
                    </>
                  )}
                </>
              ) : (
                <>
                  <XE
                    base_currency={golf_line?.supplier?.currency}
                    currency={golf_line?.supplier?.currency}
                    value={calculateCarts()}
                  />
                </>
              )}
            </Text>
          </div>
        </div>
      )}

      {transfer_line?.vehicle?.vehicle?.id && (
        <div className="flex justify-between">
          <div className="flex flex-col gap-0.5 max-w-[66%]">
            <Text size={15} color="black" className="!mb-[2px]">
              Transfers
            </Text>
            <Text size={13} leading={1.3} color="black60" className="mb-0">
              {getTransferDetails()}
            </Text>
          </div>

          <div className="flex flex-col gap-0.5">
            <Text
              size={15}
              weight="semibold"
              color="black90"
              className="!mb-[2px] text-right"
            >
              <XE
                base_currency={transfer_line?.supplier?.currency}
                currency={display_currency}
                value={calculateTransfers()}
              />
            </Text>
            {calculateTransfers() > 0 && (
              <Text
                size={13}
                color="black60"
                leading={1.3}
                className="mb-0 text-right"
              >
                {display_currency !== transfer_line?.supplier?.currency && (
                  <>
                    <XE
                      base_currency={transfer_line?.supplier?.currency}
                      currency={transfer_line?.supplier?.currency}
                      value={calculateTransfers()}
                    />
                  </>
                )}
              </Text>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CartItemPriceBreakdown;
